import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import { logger } from '../utils/logger'
import { shouldBypassSupabaseRequests } from '../lib/runtimeConfig'
import { StorageService, type BoardData } from '../services/StorageService'
import {
  clearBoardThumbnailPreview,
  readBoardThumbnailPreviews,
  subscribeToBoardThumbnailPreviews,
  type BoardThumbnailPreviewEntry,
} from '../utils/boardThumbnailPreview'

type VisionBoard = Database['public']['Tables']['vision_boards']['Row']
const storageService = new StorageService()
const boardCache = new Map<string, VisionBoard[]>()
const getBoardCacheStorageKey = (userId: string) =>
  `printboard:vision-boards:${userId}`

const readPersistedBoards = (userId?: string): VisionBoard[] => {
  if (!userId || typeof window === 'undefined') return []

  try {
    const rawValue = window.sessionStorage.getItem(
      getBoardCacheStorageKey(userId)
    )
    if (!rawValue) return []

    const parsedValue = JSON.parse(rawValue)
    return Array.isArray(parsedValue) ? (parsedValue as VisionBoard[]) : []
  } catch {
    return []
  }
}

const writePersistedBoards = (userId: string, boards: VisionBoard[]) => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(
      getBoardCacheStorageKey(userId),
      JSON.stringify(boards)
    )
  } catch {
    // Ignore storage write failures and fall back to in-memory cache only.
  }
}

const mapBoardDataToVisionBoard = (board: BoardData): VisionBoard => {
  const timestamp =
    board.updatedAt || board.createdAt || new Date().toISOString()
  let parsedCanvasData: Record<string, unknown> = {}
  if (typeof board.canvasState === 'string') {
    try {
      parsedCanvasData = JSON.parse(board.canvasState || '{}')
    } catch {
      parsedCanvasData = {}
    }
  }

  return {
    id: board.id || crypto.randomUUID(),
    user_id: board.userId,
    title: board.title,
    description: board.description || null,
    template_type: board.templateType || 'blank',
    canvas_data: parsedCanvasData,
    is_public: board.isPublic || false,
    thumbnail_url: board.thumbnail || null,
    last_saved_at: board.lastSaved || timestamp,
    parent_board_id: null,
    is_template: false,
    view_count: 0,
    tags: [],
    created_at: board.createdAt || timestamp,
    updated_at: timestamp,
  }
}

export interface UseVisionBoardsResult {
  boards: VisionBoard[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  renameBoard: (boardId: string, title: string) => Promise<boolean>
  duplicateBoard: (boardId: string) => Promise<string | null>
  deleteBoard: (boardId: string) => Promise<boolean>
}

/**
 * Hook for managing vision boards in the gallery
 * Fetches all boards for the current user and provides CRUD operations
 */
export function useVisionBoards(userId?: string): UseVisionBoardsResult {
  const [boards, setBoards] = useState<VisionBoard[]>(() =>
    userId ? boardCache.get(userId) || readPersistedBoards(userId) : []
  )
  const [loading, setLoading] = useState(() =>
    userId
      ? !boardCache.has(userId) && readPersistedBoards(userId).length === 0
      : true
  )
  const [error, setError] = useState<string | null>(null)
  const [thumbnailPreviews, setThumbnailPreviews] = useState<
    Record<string, BoardThumbnailPreviewEntry>
  >(() => (userId ? readBoardThumbnailPreviews(userId) : {}))
  const cacheKeyRef = useRef<string | null>(userId || null)

  const syncBoards = useCallback(
    (
      nextBoards:
        | VisionBoard[]
        | ((previousBoards: VisionBoard[]) => VisionBoard[])
    ) => {
      setBoards((previousBoards) => {
        const resolvedBoards =
          typeof nextBoards === 'function'
            ? nextBoards(previousBoards)
            : nextBoards

        if (cacheKeyRef.current) {
          boardCache.set(cacheKeyRef.current, resolvedBoards)
          writePersistedBoards(cacheKeyRef.current, resolvedBoards)
        }

        return resolvedBoards
      })
    },
    []
  )

  const fetchBoards = useCallback(async () => {
    try {
      // Get current user if userId not provided
      let currentUserId = userId
      if (!currentUserId) {
        if (shouldBypassSupabaseRequests) {
          cacheKeyRef.current = null
          syncBoards([])
          setLoading(false)
          return
        }
        const {
          data: { user },
        } = await supabase.auth.getUser()
        currentUserId = user?.id
      }

      if (!currentUserId) {
        logger.warn(
          'useVisionBoards',
          'No user ID available, returning empty boards'
        )
        cacheKeyRef.current = null
        syncBoards([])
        setLoading(false)
        return
      }

      cacheKeyRef.current = currentUserId
      const hasCachedBoards = boardCache.has(currentUserId)
      const persistedBoards = readPersistedBoards(currentUserId)
      const cachedBoards = hasCachedBoards
        ? boardCache.get(currentUserId) || []
        : persistedBoards

      if (cachedBoards.length === 0) {
        setLoading(true)
      } else {
        syncBoards(cachedBoards)
      }

      setError(null)

      if (shouldBypassSupabaseRequests) {
        const localResult = await storageService.listUserBoards(currentUserId)
        if (!localResult.success) {
          if (!hasCachedBoards) {
            setError(localResult.error || 'Failed to load local boards')
            syncBoards([])
          }
          setLoading(false)
          return
        }
        syncBoards((localResult.boards || []).map(mapBoardDataToVisionBoard))
        setLoading(false)
        return
      }

      logger.debug('useVisionBoards', 'Fetching boards for user', {
        userId: currentUserId,
      })

      const { data, error: fetchError } = await supabase
        .from('vision_boards')
        .select('*')
        .eq('user_id', currentUserId)
        .order('updated_at', { ascending: false })

      if (fetchError) {
        logger.error('useVisionBoards', 'Error fetching boards', fetchError)
        if (cachedBoards.length === 0) {
          setError(fetchError.message)
        }
        setLoading(false)
        return
      }

      logger.info('useVisionBoards', 'Boards fetched successfully', {
        count: data?.length || 0,
      })
      syncBoards(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logger.error('useVisionBoards', 'Exception in fetchBoards', err)
      if (
        !cacheKeyRef.current ||
        (boardCache.get(cacheKeyRef.current)?.length || 0) === 0
      ) {
        setError(errorMessage)
        syncBoards([])
      }
    } finally {
      setLoading(false)
    }
  }, [syncBoards, userId])

  const duplicateBoard = async (boardId: string): Promise<string | null> => {
    try {
      logger.info('useVisionBoards', 'Duplicating board', { boardId })

      // Find the original board
      const originalBoard = boards.find((b) => b.id === boardId)
      if (!originalBoard) {
        logger.error('useVisionBoards', 'Board not found for duplication', {
          boardId,
        })
        return null
      }

      if (shouldBypassSupabaseRequests) {
        const now = new Date().toISOString()
        const localDuplicateResult = await storageService.saveBoard({
          id: undefined,
          title: `${originalBoard.title} (Copy)`,
          description: originalBoard.description || undefined,
          templateType: originalBoard.template_type,
          canvasState: JSON.stringify(originalBoard.canvas_data || {}),
          userId: originalBoard.user_id,
          isPublic: originalBoard.is_public,
          thumbnail: originalBoard.thumbnail_url || undefined,
          createdAt: now,
          updatedAt: now,
          lastSaved: now,
        })
        if (!localDuplicateResult.success || !localDuplicateResult.boardId) {
          logger.error(
            'useVisionBoards',
            'Error duplicating local board',
            localDuplicateResult.error
          )
          return null
        }

        const duplicatedBoard: VisionBoard = {
          ...originalBoard,
          id: localDuplicateResult.boardId,
          title: `${originalBoard.title} (Copy)`,
          created_at: now,
          updated_at: now,
          last_saved_at: now,
          view_count: 0,
        }
        syncBoards((prev) => [duplicatedBoard, ...prev])
        return localDuplicateResult.boardId
      }

      // Create duplicate with new ID and updated metadata
      const duplicateData = {
        ...originalBoard,
        id: undefined, // Let Supabase generate new ID
        title: `${originalBoard.title} (Copy)`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        view_count: 0,
        // Keep the same thumbnail_url as it points to the same visual content
      }

      const { data: newBoard, error: duplicateError } = await supabase
        .from('vision_boards')
        .insert(duplicateData)
        .select()
        .single()

      if (duplicateError) {
        logger.error(
          'useVisionBoards',
          'Error duplicating board',
          duplicateError
        )
        return null
      }

      // Add to local state
      syncBoards((prev) => [newBoard, ...prev])
      logger.info('useVisionBoards', 'Board duplicated successfully', {
        originalId: boardId,
        newId: newBoard.id,
      })

      return newBoard.id
    } catch (err) {
      logger.error('useVisionBoards', 'Exception in duplicateBoard', err)
      return null
    }
  }

  const renameBoard = async (
    boardId: string,
    title: string
  ): Promise<boolean> => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      return false
    }

    try {
      logger.info('useVisionBoards', 'Renaming board', { boardId })

      if (shouldBypassSupabaseRequests) {
        const board = boards.find((b) => b.id === boardId)
        if (!board) return false

        const result = await storageService.saveBoard({
          id: board.id,
          userId: board.user_id,
          title: trimmedTitle,
          description: board.description || undefined,
          templateType: board.template_type,
          canvasState: JSON.stringify(board.canvas_data || {}),
          isPublic: board.is_public,
          thumbnail: board.thumbnail_url || undefined,
          createdAt: board.created_at,
          updatedAt: new Date().toISOString(),
          lastSaved: board.last_saved_at || new Date().toISOString(),
        })

        if (!result.success) {
          logger.error(
            'useVisionBoards',
            'Error renaming local board',
            result.error
          )
          return false
        }

        syncBoards((prev) =>
          prev.map((board) =>
            board.id === boardId
              ? {
                  ...board,
                  title: trimmedTitle,
                  updated_at: new Date().toISOString(),
                }
              : board
          )
        )
        return true
      }

      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        logger.error(
          'useVisionBoards',
          'No authenticated user for rename operation'
        )
        return false
      }

      const { error: updateError } = await supabase
        .from('vision_boards')
        .update({
          title: trimmedTitle,
          updated_at: new Date().toISOString(),
        })
        .eq('id', boardId)
        .eq('user_id', user.id)

      if (updateError) {
        logger.error('useVisionBoards', 'Error renaming board', updateError)
        return false
      }

      syncBoards((prev) =>
        prev.map((board) =>
          board.id === boardId
            ? {
                ...board,
                title: trimmedTitle,
                updated_at: new Date().toISOString(),
              }
            : board
        )
      )

      return true
    } catch (err) {
      logger.error('useVisionBoards', 'Exception in renameBoard', err)
      return false
    }
  }

  const deleteBoard = async (boardId: string): Promise<boolean> => {
    try {
      logger.info('useVisionBoards', 'Deleting board', { boardId })

      if (shouldBypassSupabaseRequests) {
        const board = boards.find((b) => b.id === boardId)
        if (!board) {
          return false
        }
        const result = await storageService.deleteBoard(boardId, board.user_id)
        if (!result.success) {
          logger.error(
            'useVisionBoards',
            'Error deleting local board',
            result.error
          )
          return false
        }
        syncBoards((prev) => prev.filter((b) => b.id !== boardId))
        return true
      }

      // Get current user ID
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        logger.error(
          'useVisionBoards',
          'No authenticated user for delete operation'
        )
        return false
      }

      const { error: deleteError } = await supabase
        .from('vision_boards')
        .delete()
        .eq('id', boardId)
        .eq('user_id', user.id)

      if (deleteError) {
        logger.error('useVisionBoards', 'Error deleting board', deleteError)
        return false
      }

      // Remove from local state
      syncBoards((prev) => prev.filter((b) => b.id !== boardId))
      logger.info('useVisionBoards', 'Board deleted successfully', { boardId })

      return true
    } catch (err) {
      logger.error('useVisionBoards', 'Exception in deleteBoard', err)
      return false
    }
  }

  // Fetch boards on mount and when userId changes
  useEffect(() => {
    fetchBoards()
  }, [userId, fetchBoards])

  useEffect(() => {
    if (!userId) {
      setThumbnailPreviews({})
      return
    }

    setThumbnailPreviews(readBoardThumbnailPreviews(userId))

    return subscribeToBoardThumbnailPreviews({
      userId,
      onSet: (entry) => {
        setThumbnailPreviews((prev) => ({
          ...prev,
          [entry.boardId]: entry,
        }))
      },
      onClear: ({ boardId, persistedThumbnailUrl }) => {
        setThumbnailPreviews((prev) => {
          if (!(boardId in prev)) return prev

          const next = { ...prev }
          delete next[boardId]
          return next
        })

        if (persistedThumbnailUrl) {
          syncBoards((prev) =>
            prev.map((board) =>
              board.id === boardId
                ? {
                    ...board,
                    thumbnail_url: persistedThumbnailUrl,
                  }
                : board
            )
          )
        }
      },
    })
  }, [syncBoards, userId])

  // Set up real-time subscription for board changes
  useEffect(() => {
    if (shouldBypassSupabaseRequests) {
      return
    }
    if (!userId) return

    logger.info('useVisionBoards', 'Setting up realtime subscription', {
      userId,
    })

    const channel = supabase
      .channel('vision_boards_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vision_boards',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.info('useVisionBoards', 'Realtime update received', {
            eventType: payload.eventType,
            boardId: payload.new?.id || payload.old?.id,
          })

          switch (payload.eventType) {
            case 'INSERT':
              if (payload.new) {
                syncBoards((prev) => [payload.new as VisionBoard, ...prev])
              }
              break
            case 'UPDATE':
              if (payload.new) {
                const nextBoard = payload.new as VisionBoard
                let thumbnailDidChange = false

                syncBoards((prev) =>
                  prev.map((board) => {
                    if (board.id !== nextBoard.id) return board

                    thumbnailDidChange =
                      board.thumbnail_url !== nextBoard.thumbnail_url
                    return nextBoard
                  })
                )

                if (thumbnailDidChange && nextBoard.thumbnail_url) {
                  clearBoardThumbnailPreview({
                    userId,
                    boardId: nextBoard.id,
                    persistedThumbnailUrl: nextBoard.thumbnail_url,
                  })
                }
              }
              break
            case 'DELETE':
              if (payload.old) {
                syncBoards((prev) =>
                  prev.filter((board) => board.id !== payload.old.id)
                )
              }
              break
          }
        }
      )
      .subscribe()

    return () => {
      logger.info('useVisionBoards', 'Cleaning up realtime subscription')
      supabase.removeChannel(channel)
    }
  }, [syncBoards, userId])

  const mergedBoards = useMemo(
    () =>
      boards.map((board) => {
        const previewEntry = thumbnailPreviews[board.id]
        if (!previewEntry) return board

        return {
          ...board,
          thumbnail_url: previewEntry.previewUrl,
        }
      }),
    [boards, thumbnailPreviews]
  )

  return {
    boards: mergedBoards,
    loading,
    error,
    refetch: fetchBoards,
    renameBoard,
    duplicateBoard,
    deleteBoard,
  }
}
